import { transition, trigger, useAnimation } from '@angular/animations';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Subscription } from 'rxjs';

import { AppGalleryConfigService } from '../app-gallery/app-gallery-config.service';
import { linearFadeOut } from '../shared/animations/animations';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { CreateMenuService } from '../shared/create-menu/create-menu.service';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { PaintMarkerName } from '../shared/models/paint-marker-name.enum';
import { Workload } from '../shared/models/workload.model';
import { BrowserPaintMeasurementService } from '../shared/services/browser-paint-measurement.service';
import { BrowserPaintService } from '../shared/services/browser-paint.service';
import { ConsumerSkuType } from '../shared/services/consumer-license.service';
import { DeviceDetector } from '../shared/services/device-detector.service';
import { FloodgateService } from '../shared/services/floodgate.service';
import { LocalStorageService } from '../shared/services/local-storage.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { PerformanceService } from '../shared/services/performance.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../shared/shared-context/consumer-install-config.service';
import { OneDriveUploadService } from '../upload/upload.service';
import { UnpinWorkloadEvent } from './app-tile-state-event.model';
import { AppTilesConfigService } from './shared/app-tiles-config.service';

import './app-tiles-list.component.css';

const enum AppTilesState {
    All = 'All',   // All tiles are displayed.
    Reduced = 'Reduced' // Only Reduced set of tiles are displayed.
}

@Component({
    selector: 'ohp-app-tiles-list',
    templateUrl: './app-tiles-list.component.html',
    animations: [
        trigger('fadeOut', [
            transition(':leave', useAnimation(linearFadeOut))
        ])
    ]
})
export class AppTilesListComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChildren('appGalleryLink') public appGalleryLink: QueryList<ElementRef>;

    @Input('fullBleedTheming') public fullBleedTheming: boolean = false;

    @Input('fullBleedFontColor') public fullBleedFontColor: string = '';

    @Input('fullBleedBackplateEnabled') public fullBleedBackplateEnabled: boolean = false;

    @Input('fullBleedBackplateStyle') public fullBleedBackplateStyle: { [key: string]: string } = {};

    public appTilesLandmarkAriaLabel: string;
    public componentArea: string;
    public exploreYourAppsAriaLabel: string;
    public exploreYourAppsText: string;
    public workloads: Array<Readonly<Workload>>;
    public sectionTitle: string;
    public showProvisioningAlert: boolean;
    public provisioningAlertText: string;
    public promotedAppTile: Workload;
    public promotedAppTitle: string;
    public showLessAppTilesText: string;
    public promotedIndex: number;
    public showAppGalleryLink: boolean;
    public rectangularMobileMaxInitialItems = 4;
    public reducedTilesCount: number;
    public myAccountLinkText: string;
    public isConsumer: boolean;
    public isPaidConsumer: boolean;
    public myAccountUrl: string;
    public pinnedAppsEnabled: boolean;
    public bottomTrayEnabled: boolean;
    public appGalleryTileText: string;
    public isMobile: boolean;
    public showCreatePlaceholder: boolean;
    public createPlaceholderText: string;
    public appGalleryRoute = OfficeRoute.AppGallery;
    public createMenuEnabled = false;
    public scaleTile: boolean;

    private static loggedMarker = false;

    private appTilesState: AppTilesState;
    private appGalleryLinkSubscription: Subscription;
    private previousRouteSubscription: Subscription;
    private previousRoute: OfficeRoute;
    private thirdPartyWorkloadsSubscription: Subscription;
    private showingCreateMenu: boolean;

    constructor(
        private readonly appGalleryConfigService: AppGalleryConfigService,
        private readonly appTilesConfigService: AppTilesConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly deviceDetector: DeviceDetector,
        private readonly floodgateService: FloodgateService,
        private readonly instrumentationService: InstrumentationService,
        private readonly performanceService: PerformanceService,
        private readonly officeRouteService: OfficeRouteService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly workloadsService: WorkloadsService,
        private readonly browserPaintMeasurementService: BrowserPaintMeasurementService,
        private readonly localStorageService: LocalStorageService,
        private readonly createMenuService: CreateMenuService,
        private readonly oneDriveUploadService: OneDriveUploadService,
        private readonly browserPaintService: BrowserPaintService,
        private readonly changeDetector: ChangeDetectorRef
    ) {
    }

    public ngAfterViewInit() {
        // Log the time passed since user started navigation to office.com until tiles are rendered.
        // Ensure that this is done only once.
        if (!AppTilesListComponent.loggedMarker) {
            this.performanceService.mark(KnownInstrumentationEvents.AppTilesReady);
            AppTilesListComponent.loggedMarker = true;
        }

        this.previousRouteSubscription = this.officeRouteService.subscribeToRouteChangeEnd(
            event => this.previousRoute = event.previousRoute ? event.previousRoute.route : null);

        // Focus on App Gallery link when navigating back to home page.
        this.appGalleryLinkSubscription = this.appGalleryLink.changes.subscribe(links => {
            if (this.previousRoute === OfficeRoute.AppGallery && links && links.length === 1) {
                // Run after paint to avoid synchronous layout computation.
                this.browserPaintService.runAfterPaint(() => {
                    links.first.nativeElement.focus();
                    this.appGalleryLinkSubscription.unsubscribe();
                });
            }
        });

        this.browserPaintMeasurementService.recordWhenPainted(PaintMarkerName.App_Tiles);

        const isLeftNav = this.capabilitiesService.capabilities.leftNavReact;
        if (!isLeftNav) {
            this.floodgateService.recordFloodgate(['SawLeftNav']);
            // This is just for users of first release but on the left nav control flight (NOT seeing left nav)
            // to start seeing floodgate survey as a basis.
            // This needs to use the same event name as other floodgate event in left-nav-react-content.directive
        }
    }

    public ngOnInit() {
        this.componentArea = KnownAreas.AppTiles;
        const config = this.appTilesConfigService.config;
        const resources = config.resources;
        this.sectionTitle = resources && resources.AppTilesHeader;
        this.provisioningAlertText = resources && resources.ProvisioningAlertText;
        this.showLessAppTilesText = resources && resources.ShowLessAppTiles;
        this.exploreYourAppsAriaLabel = resources && resources.ExploreYourAppsAriaLabel;
        this.exploreYourAppsText = resources && resources.ExploreYourAppsText;
        this.appGalleryTileText = resources && resources.AppGalleryTileText;
        this.appTilesLandmarkAriaLabel = resources && resources.AppTilesListLandmarkAriaLabel;
        this.myAccountLinkText = resources && resources.MyAccountConsumerLinkText;
        this.myAccountUrl = this.appTilesConfigService.config.myAccountUrl;
        this.isConsumer = this.startupConfigService.config.authType === AuthType.MSA;
        this.bottomTrayEnabled = this.capabilitiesService.capabilities.bottomTrayEnabled;
        this.isMobile = this.deviceDetector.isMobileDevice();
        this.pinnedAppsEnabled = this.appTilesConfigService.config.pinnedTilesEnabled;
        this.scaleTile = config.hoverScalingEnabled;

        if (this.isConsumer) {
            this.consumerInstallConfigService.getConfig().then(consumerInstallConfig => {
                const subscriptionStatus = consumerInstallConfig && consumerInstallConfig.currentSkuInfo ? consumerInstallConfig.currentSkuInfo.skuType : ConsumerSkuType.NotDetected;
                this.isPaidConsumer = !(subscriptionStatus === ConsumerSkuType.NotSubscribed || subscriptionStatus === ConsumerSkuType.NonActive || subscriptionStatus === ConsumerSkuType.NotDetected);
            });
        }

        if (this.capabilitiesService.capabilities.createButtonWithAnimationEnabled) {
            this.createMenuEnabled = false;
            this.showCreatePlaceholder = false;
        } else {
            this.createMenuService.createButtonEnabledPromise.then(createButtonEnabled => {
                this.createMenuEnabled = this.createMenuEnabled || createButtonEnabled;
                this.showCreatePlaceholder = this.createMenuEnabled && !this.showingCreateMenu;
            });

            this.oneDriveUploadService.uploadEnabledPromise.then(uploadEnabled => {
                this.createMenuEnabled = this.createMenuEnabled || uploadEnabled;
                this.showCreatePlaceholder = this.createMenuEnabled && !this.showingCreateMenu;
            });
        }

        this.initWorkloads();
    }

    public ngOnDestroy() {
        if (this.appGalleryLinkSubscription) {
            this.appGalleryLinkSubscription.unsubscribe();
        }

        if (this.previousRouteSubscription) {
            this.previousRouteSubscription.unsubscribe();
        }

        if (this.thirdPartyWorkloadsSubscription) {
            this.thirdPartyWorkloadsSubscription.unsubscribe();
        }
    }

    public async onUnpinWorkload(event: UnpinWorkloadEvent) {
        await this.workloadsService.unpinWorkload(event.item);

        // TODO: Switch to using observables instead of modifying local workloads list.
        this.workloads = this.workloads.filter(workload => !(workload.AppId === event.item.AppId && workload.CollectorId === event.item.CollectorId));

        this.changeDetector.detectChanges();
    }

    public trackByWorkloadId(_index: number, workload: Workload) {
        return workload.Id;
    }

    public isShowingReducedAppTiles() {
        return this.appTilesState === AppTilesState.Reduced;
    }

    public getDisplayedAppTilesCount(): number {
        return this.isShowingReducedAppTiles() ? Math.min(this.reducedTilesCount, this.workloads.length) : this.workloads.length;
    }

    public navigateToAppsPage(): void {
        this.officeRouteService.navigateTo({ route: OfficeRoute.AppGallery });
    }

    public removePlaceholder(): void {
        this.showCreatePlaceholder = false;
        this.showingCreateMenu = true;
    }

    private initWorkloads(): void {
        if (this.isConsumer) {
            this.workloadsService.getCoreSegmentAndFrequentApps().then((workloads: Array<Readonly<Workload>>) => this.setupWorkloads(workloads));
        } else {
            if (this.pinnedAppsEnabled) {
                // Do this only for pinned apps
                this.setupWorkloads(this.workloadsService.getPinnedWorkloads());
            } else {
                this.workloadsService.getWorkloads().then((workloads: Array<Readonly<Workload>>) => this.setupWorkloads(workloads));
            }
        }
    }

    private async setupWorkloads(workloads: Array<Readonly<Workload>>) {
        const config = this.appTilesConfigService.config;

        this.workloads = workloads.map(workload =>
            // Shell passes #0072c6 as color for all workloads without a predefined color
            ({
                ...workload
            })
        );

        if (!!this.appGalleryConfigService.config) {
            if (this.workloads.length > 0) {
                this.showAppGalleryLink = true;
            } else {
                this.thirdPartyWorkloadsSubscription = this.workloadsService.getThirdPartyWorkloads().subscribe(thirdPartyWorkloads => {
                    if (thirdPartyWorkloads.length > 0) {
                        this.showAppGalleryLink = true;
                        this.thirdPartyWorkloadsSubscription.unsubscribe();
                    }
                });
            }
        }

        this.showProvisioningAlert = this.workloads.some(workload => !workload.Url);
        this.promotedIndex = -1;
        let clickedOneApp = false;

        this.workloads.forEach((workload, index) => {
            // No promoted app tile for edge NTP experience or when app swipe is enabled
            if (!config.isPromotedAppTileExpired &&
                config.promotedAppTileId &&
                workload.Id.toLowerCase() === config.promotedAppTileId.toLowerCase() &&
                !this.isMobile
            ) {
                this.promotedIndex = index;
                this.setPromotedApp(workload, config.resources && config.resources.PromotedAppSectionTitle);
            }
            if (workload.ClickCount > 0 && this.capabilitiesService.capabilities.showFloodgate && this.capabilitiesService.capabilities.officeHomeFloodgate && !clickedOneApp) {
                clickedOneApp = true;
            }
        });

        if (clickedOneApp) {
            this.floodgateService.recordFloodgate(['ClickedOneApp']);
        }

        if (this.promotedAppTile) {
            this.workloads.splice(this.promotedIndex, 1);
            this.promotedIndex = -1;

            if (config.expirePromotedAppTileEnabled) {
                this.logPromotedAppTileImpression();
            }
        }

        if (this.isMobile) {
            this.sortTiles(config.mobileAppIds);
        }

        // NOTE: when commercial and !mobile in sovereign case, this.workloads are sorted in a descending order based on click counts
        if (!this.pinnedAppsEnabled) {
            await this.setUpReducedTiles();
        }

        if (!this.isConsumer) {
            if (this.showAppGalleryLink) {
                this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.ExploreAllYourAppsImpression);
            }
        }

        // Adding native only workloads which are installed and used to workload list.
        this.workloadsService.getAdditionalWorkloads().then((additionalWorkloads: Array<Workload>) => {
            if (additionalWorkloads && additionalWorkloads.length > 0) {
                additionalWorkloads = additionalWorkloads.filter(workload => !workload.IsNativeClientOnlyApp || this.localStorageService.get(`AppClicked_${workload.Id}`));
                this.workloadsService.populateWorkloadFields(additionalWorkloads);
                this.workloads.splice(this.reducedTilesCount, 0, ...additionalWorkloads);
                this.reducedTilesCount += additionalWorkloads.length;
            }
        });
    }

    private async setUpReducedTiles(): Promise<void> {
        if (!this.workloads) {
            return;
        }

        return this.workloadsService.getCoreSegmentAndFrequentApps().then((appsAndWorkloads: Array<Workload>) => {
            this.appTilesState = AppTilesState.Reduced;
            this.reducedTilesCount = appsAndWorkloads.length;
            this.logImpression(this.getDisplayedAppTilesCount());
        });
    }

    private sortTiles(orderedAppIds: Array<string>): void {
        if (!orderedAppIds) {
            return;
        }
        // since sort here is inplace, need to keep the orginal workloads order to preserve the original ordering of the sorted tiles for the apps not included in orderedAppIds
        const originalWorkloads = this.workloads.slice(0, this.workloads.length);

        this.workloads = this.workloads.sort((first, second) => {
            // Prioritize based top apps list
            for (let i = 0; i < orderedAppIds.length; i++) {
                const appId = orderedAppIds[i];
                if (first.Id === appId) {
                    return -1;
                } else if (second.Id === appId) {
                    return 1;
                }
            }

            // Finally, prioritize based on the original list
            for (let i = 0; i < originalWorkloads.length; i++) {
                if (originalWorkloads[i] === first) {
                    return -1;
                } else if (originalWorkloads[i] === second) {
                    return 1;
                }
            }

            return 0;
        });
    }

    private logImpression(numRenderedItems: number) {
        let items = [];
        if (this.workloads) {
            items = this.workloads.slice(0); // Copy the items and include promoted app tile for log impression
        }

        if (this.promotedAppTile) {
            items.unshift(this.promotedAppTile);
            numRenderedItems++;
        }

        const distributionSeparator = ';';
        const calcDistribution = (forProvisionedApp: boolean) =>
            items.reduce((combined, current, index) => {
                if (index < numRenderedItems) {
                    return (forProvisionedApp && current.Url) || (!forProvisionedApp && !current.Url)
                        ? combined + (combined.length === 0 ? '' : distributionSeparator) + current.Id
                        : combined;
                } else {
                    return combined;
                }
            }, '');

        const params: InstrumentationParams = {};
        params[InstrumentationParam.RenderSize] = numRenderedItems.toString();
        params[InstrumentationParam.Distribution] = calcDistribution(true);
        const provisioningDistribution = calcDistribution(false);
        params[InstrumentationParam.Provisioning] = provisioningDistribution;
        const viewMode = this.isMobile ? 'Mobile' : 'Default';
        params[InstrumentationParam.State] = viewMode;
        params[InstrumentationParam.TotalSize] = this.workloads ? this.workloads.length.toString() : '0';
        params[InstrumentationParam.Action] = this.appTilesState;

        // Not logging any metrics because app tiles section is always displayed inside home page.
        // Home_PageView metric can be used as substitute when determing first/last occurrence
        // of app tiles section impression
        this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.AppTilesImpression, params);
    }

    private logPromotedAppTileImpression() {
        const params: InstrumentationParams = {};
        const viewMode = this.isMobile ? 'Mobile' : 'Default';
        params[InstrumentationParam.State] = viewMode;

        this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.PromotedAppTileImpression, params);
    }

    private setPromotedApp(workload: Workload, promotedAppTitle: string) {
        this.promotedAppTile = workload;
        this.promotedAppTitle = promotedAppTitle;
    }
}
